import {
    OtaName,
    ResourceType,
    useCreateProductVariant,
    useOtaInfo,
    useProductVariants,
} from '@travelity/api';
import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    List,
    ListItems,
    ListMain,
    ListTitle,
} from '../../components/list-layout';
import { ProductVariantCard } from './components/product-variant-card';
import { useProductVariantModal } from '../../components/product-variant-modal/product-variant-modal';
import { useResourcePermissions } from '../../contexts/user';

const ProductVariants: FC = () => {
    const { otaId } = useParams<{ otaId: OtaName }>();
    const { t } = useTranslation();
    const {
        data: variants,
        isLoading,
        isFetching,
        refetch,
    } = useProductVariants(otaId);
    const { ota } = useOtaInfo(otaId);
    const permissions = useResourcePermissions(ResourceType.PRODUCT_VARIANT);
    const showCreate = permissions.create;
    const { createProductVariant, ProductVariantModal, closeModal } =
        useProductVariantModal();
    const { mutate: createVariant, isLoading: isCreating } =
        useCreateProductVariant({
            onSuccess: () => {
                closeModal();
            },
        });

    return (
        <>
            <List>
                <ListMain
                    isLoading={isLoading || isFetching}
                    SkeletonComponent={ProductVariantCard.Skeleton}
                >
                    <ListTitle title={ota?.name}>
                        {showCreate && (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={createProductVariant}
                            >
                                {t('Create Product Variant')}
                            </Button>
                        )}
                    </ListTitle>
                    <ListItems
                        noItemsText={t('No product variants')}
                        items={variants?.length}
                        isLoading={isLoading || isFetching}
                        entityName="variant"
                        entityNamePlural="variants"
                        subtractHeight={72}
                    >
                        {variants?.map(variant => (
                            <ProductVariantCard
                                key={variant.id}
                                variant={variant}
                                onDelete={refetch}
                            />
                        ))}
                    </ListItems>
                </ListMain>
            </List>
            <ProductVariantModal
                isLoading={isCreating}
                onConfirm={data => {
                    createVariant({
                        ...data,
                        integration: { id: ota?.uuid, name: ota?.id },
                    });
                }}
                onClose={() => {
                    closeModal();
                }}
            />
        </>
    );
};

export default ProductVariants;
